import { DestroyRef, Directive, computed, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { transferArrayItem } from '@angular/cdk/drag-drop';

import { map, of, switchMap } from 'rxjs';

import { Widgets } from '@common/enums';

import {
    columnWidth as columnWidthOf,
    sectionColumnGap,
    sectionGapHalfVar,
    sectionIsFlexibleWidths,
} from '@sites/entity-core';

import type { DenormalizedColumnEntity, DenormalizedSectionEntity, NormalizedColumnEntity } from '@web/types';
import type { AddEvent } from '@web/layout/content/page-content/components/add-buttons/add-buttons.component';
import type { HoverState } from '@web/layout/content/page-content/directives/single-hover.directive';

import { instanceId } from '@web/layout/content/page-content/helpers';

import { ProductRestrictionFacade } from '@web/core/services/product-restriction.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { WebColumnFormService } from '@web/core/services/web-column-form.service';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';

const MAX_EQUAL_COLUMN_COUNT = 4;
const MAX_COLUMN_COUNT = 6;

@Directive({})
export abstract class BaseSectionComponent<T extends DenormalizedSectionEntity = DenormalizedSectionEntity> {
    public readonly entity = input.required<T>();
    public readonly index = input.required<number>();
    public readonly hasSectionActiveColumn = input<boolean>(false);
    public readonly isLastSection = input<boolean>(false);
    public readonly parentEntityId = input<string>('');

    private readonly layoutFacade = inject(LayoutFacade);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly sectionsFacade = inject(SectionsFacade);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly columnFormService = inject(WebColumnFormService);
    private readonly destroyRef = inject(DestroyRef);

    /** One entry per column id this row has resized — see {@link columnForm}. */
    private readonly columnForms = new Map<string, ReturnType<WebColumnFormService['getOrCreateScoped']>>();
    private readonly productRestrictionFacade = inject(ProductRestrictionFacade);

    public readonly hideControls = toSignal(this.layoutFacade.hideControls$, { initialValue: false });
    /** §7's "preview mode" — see the same field on `BaseBlockComponent` for why `hovered()` is the one gate. */
    protected readonly isCanvasPreviewActive = toSignal(this.layoutFacade.isCanvasPreviewActive$, {
        initialValue: false,
    });

    /** Drop targets for the column drag — every non-specific section on the page. */
    public readonly connectedTo = toSignal(this.blocksFacade.sectionConnectedToList$, { initialValue: [] as string[] });

    /**
     * Restriction and parent-block lookups follow the section. The parent-block
     * one used to be a `take(1)` snapshot re-run from `ngOnInit`/`ngOnChanges`,
     * so it went stale whenever the block's width mode changed.
     */
    private readonly section$ = toObservable(this.entity);

    public readonly isSectionRestricted = toSignal<boolean | null>(
        this.section$.pipe(switchMap((section) => this.productRestrictionFacade.sectionRestricted$(section))),
        { initialValue: null }
    );

    public readonly isParentBlockFullWidth = toSignal(
        this.section$.pipe(
            switchMap((section) => (section?.parentId ? this.blocksFacade.blockById$(section.parentId) : of(null))),
            // `100vw` is what the old `isFullWidth` boolean meant; a zero block
            // has no width of its own, hence the `in` guard rather than a cast.
            map((block) => Boolean(block && 'width' in block.props && block.props.width?.unit === 'vw'))
        ),
        { initialValue: false }
    );

    public readonly hoverState = signal<HoverState | null>(null);

    public readonly instanceId = instanceId;

    public readonly columnCount = computed<number>(() => this.entity().children.length);
    public readonly columnsCountReachedMax = computed<boolean>(() => this.columnCount() >= MAX_COLUMN_COUNT);

    public readonly hasCatalogProductsWidget = computed<boolean>(() =>
        this.entity().children.some((column) =>
            column.children?.some((widget) => widget?.name === Widgets.CATALOG_PRODUCTS)
        )
    );

    public hovered(event: HoverState): void {
        this.hoverState.set(this.isCanvasPreviewActive() ? null : event);
    }

    /**
     * A full row takes no columns from other rows — the same limit that disables
     * "copy". Rejected here, the dragged column never previews in the row and
     * snaps back to where it came from.
     */
    public readonly canEnterColumn = (drag: CdkDrag, drop: CdkDropList): boolean =>
        drag.dropContainer === drop || !this.columnsCountReachedMax();

    public dropColumn(event: CdkDragDrop<DenormalizedColumnEntity[]>): void {
        const { item, container, previousContainer, currentIndex, previousIndex } = event;

        if (container === previousContainer && previousIndex === currentIndex) {
            return;
        }

        if (container !== previousContainer && this.columnsCountReachedMax()) {
            return;
        }

        const connectedTo = this.connectedTo();
        if (!connectedTo.includes(container.id) || !connectedTo.includes(previousContainer.id)) {
            return;
        }

        const prevSectionId = (item.data as DenormalizedColumnEntity).parentId;
        const columnId = (item.data as DenormalizedColumnEntity).id;
        const newSectionId = container.id;
        transferArrayItem([...previousContainer.data], [...container.data], previousIndex, currentIndex);
        this.columnsFacade.moveColumnToSection(columnId, prevSectionId, newSectionId, previousIndex, currentIndex);
    }

    public addSection(event: AddEvent): void {
        this.sectionsFacade.createSection(this.entity().parentId, this.index() + event.indexOffset);
    }

    public addColumn(event: AddEvent): void {
        const columns = this.entity().children;

        this.columnsFacade.createColumn(
            this.entity().id,
            event.indexOffset ? columns.length + event.indexOffset : event.indexOffset
        );
    }

    /**
     * Column widths are stored either as twelfths of the grid or as percentages
     * — `columnWidthFlex` picks which, and the resize handle nudges the pair by
     * one unit of whichever it is.
     */
    /** Only an ordinary row has the flag; header and zero rows never do. */
    public isFlexibleWidths(): boolean {
        return sectionIsFlexibleWidths(this.entity());
    }

    /**
     * The resize handle moves one unit from one neighbour to the other. The unit
     * travels with the width, so only the number changes.
     */
    /**
     * Scoped to the row, so each column's form is ref-counted once rather than on
     * every step of a drag.
     */
    private columnForm(id: string) {
        const existing = this.columnForms.get(id);
        if (existing) {
            return existing;
        }

        const form = this.columnFormService.getOrCreateScoped(id, this.destroyRef);
        this.columnForms.set(id, form);

        return form;
    }

    /**
     * Moves one unit from one neighbour to the other, through each column's own
     * form. The unit travels with the width, so only the number changes.
     *
     * The previous version built two `Update`s and dispatched them, which meant
     * the forms of the columns being resized kept the pre-drag widths and wrote
     * them back on the next edit. It also recorded history for the left drag only,
     * so dragging one way was undoable and the other way was not; the form's own
     * batch covers both.
     */
    private moveWidth(column: DenormalizedColumnEntity, delta: number): void {
        // A zero row's column fills the block and has no width to move.
        if (column.entitySubType === 'zero') {
            return;
        }

        const form = this.columnForm(column.id);

        if ('setWidth' in form) {
            form.setWidth(column.props.width.value + delta);
        }
    }

    public changeColumnWidthToLeft(colLeft: DenormalizedColumnEntity, colRight: DenormalizedColumnEntity): void {
        this.moveWidth(colLeft, -1);
        this.moveWidth(colRight, 1);
    }

    public changeColumnWidthToRight(colLeft: DenormalizedColumnEntity, colRight: DenormalizedColumnEntity): void {
        this.moveWidth(colLeft, 1);
        this.moveWidth(colRight, -1);
    }

    /** The row's gap doubles as the vertical margin of every column in it. */
    public readonly columnGapHalf = computed<string>(() =>
        sectionGapHalfVar({ value: sectionColumnGap(this.entity()), unit: 'px' })
    );

    public getColumnClasses(column: DenormalizedColumnEntity): string {
        const columnCount = this.columnCount();

        if (columnCount > MAX_EQUAL_COLUMN_COUNT || this.isFlexibleWidths()) {
            return `lpc-col-equal-${columnCount}`;
        }

        const columnWidth = columnWidthOf(column) || 12 / columnCount;

        return `lpc-col-${columnWidth} lpc-col-equal-${columnCount}`;
    }

    public getColumnWidth(column: DenormalizedColumnEntity): string {
        if (!this.isFlexibleWidths()) {
            return;
        }

        return `${columnWidthOf(column) || 100 / this.columnCount()}%`;
    }
}
